import {
    Component,
    EventEmitter,
    Output,
    ElementRef,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { LeadService } from 'app/global-services/lead.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DBC_SCREENS } from 'app/app.constants';
import _ from 'lodash';
import { AuthService } from 'app/global-services/auth.service';
import { Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { User } from 'app/user-account/user.model';

interface LeadUserType {
    id: string,
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    designation: string;
    company: string;
    avatar_url: string;
    created: string;
}

enum LEAD_LIST_STATE {
    FETCHED = 'fetched',
    LOADING = 'loading',
    ERROR = 'error',
}

enum CONTACT_LIST_FILTER {
    ALPHABET_ASCENDING = 'alphabet-ascending',
    ALPHABET_DESCENDING = 'alphabet-descending',
    CALENDAR_ASCENDING = 'calendar-ascending',
    CALENDAR_DESCENDING = 'calendar-descending',
}

enum LEAD_LIST_FILTER_STATE {
    ALPHABET = 'alphabet',
    CALENDAR = 'calendar',
}

enum CONTACT_TYPE {
    ORGANIZATION_CONTACTS = 'Organization Contacts',
    MY_CONTACTS = 'My Contacts'
}

@Component({
    selector: 'app-lead-list',
    templateUrl: './lead-list.component.html',
    styleUrl: './lead-list.component.scss',
    standalone: false
})
export class LeadListComponent {
    @Output() closeScreen = new EventEmitter();
    @ViewChildren('contactItemsElements')
    contactItemsElements: QueryList<ElementRef>;

    
    LEAD_LIST_STATE = LEAD_LIST_STATE;
    LEAD_LIST_FILTER_STATE = LEAD_LIST_FILTER_STATE;
    CONTACT_LIST_FILTER = CONTACT_LIST_FILTER;
    DBC_SCREENS = DBC_SCREENS;

    protected readonly navigator = navigator;

    leadListState: LEAD_LIST_STATE = LEAD_LIST_STATE.LOADING;
    leadListFilterState: LEAD_LIST_FILTER_STATE =
        LEAD_LIST_FILTER_STATE.ALPHABET;
    isLoggedIn$: Observable<boolean>;

    // store the data for passing to lead view
    leadViewData = {};
    leadViewToggle = false;
    filterToggle = false;
    filter = CONTACT_LIST_FILTER.ALPHABET_ASCENDING;
    isLeadCountZero = false;

    // flag for checking admin account
    CONTACT_TYPE = CONTACT_TYPE;
    adminContactTypeFilter = CONTACT_TYPE.MY_CONTACTS;
    isAdminUser = false;
    isAdminDropdownOpen = false;

    user: User;
    ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        private readonly leadService: LeadService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly authService: AuthService,
        private readonly router: Router,
    ) {

        this.isLoggedIn$ = this.authService.isLoggedIn$;
        // Update the login state to error when user is not logged in
        this.isLoggedIn$.subscribe(isLoggedIn => {
            if (!isLoggedIn || !navigator.onLine) {
                this.switchLeadListState(LEAD_LIST_STATE.ERROR);
            }
        });

        this.authService.user$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
            if (res) {
                this.user = res;
                this.isAdminUser = this.user.isAdmin(res.id);
            }
        });
    }

    // Storage of sorting Data
    leadData = {};

    // Alphabet letters array
    alphabet: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    @ViewChildren('alphabetElement') alphabetElements!: QueryList<ElementRef>;

    ngAfterViewInit() {
        if(navigator.onLine && this.isLoggedIn$) {
            this.initiateLeadData();
        }
    }

    // Initiate Lead Data on Login
    private initiateLeadData() {
        const initialStateForLeads =  setInterval(() => {
            if(this.isLoggedIn$) {
                this.getAllLeadUser();
                clearInterval(initialStateForLeads);
            }
        }, 100);
    }

    // Handle behaviour if user is coming from capture screen
    private handleLeadCreationRedirection() {
            this.activatedRoute.queryParams.pipe(
                switchMap((params)=> {
                    if(params['lead_id']) {
                        this.scrollToParticularLead(params['lead_id']);
                        return of(true)
                    }
                    return of(false)
                }),
            ).subscribe((response)=> {
                if(response) {
                    this.router.navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: { lead_id: undefined },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                        fragment: this.activatedRoute.snapshot.fragment,
                    });
                }
            });
    }

    private scrollToParticularLead(leadId: string) {
        const targetElement = this.contactItemsElements.find(
            (el) => el.nativeElement.getAttribute('data-lead-id') === leadId
        );
        if (targetElement) {
            targetElement.nativeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setTimeout(() => {
                targetElement.nativeElement.click();
            }, 1000);
        } else {
            console.warn('No matching element found for email:', leadId);
        }
    }

    // Handle A-Z Navigator Filter
    scrollToAlphabet(letter: string) {
        const targetId = `alphabet-${letter}`;
        const targetElement = this.alphabetElements.find(
            (el) => el.nativeElement.id === targetId
        );
        if (targetElement) {
            targetElement.nativeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        } else {
            console.warn('No matching element for ID:', targetId);
        }
    }

    objectKeys(obj: any) {
        return _.keys(obj);
    }

    // Specific Contact View
    openLeadView(leadViewData: LeadUserType) {
        this.leadViewData = leadViewData;
        this.leadViewToggle = true;
        this.filterToggle = false;
        this.isAdminDropdownOpen = false;
    }

    // Close Contact View
    closeLeadView() {
        this.leadViewData = {};
        this.leadViewToggle = false;
    }

    handleRefreshOnLeadList() {
        this.getAllLeadUser();
    }
    
    handleAdminContactList(passedAdminFilter:CONTACT_TYPE) {
        this.adminContactTypeFilter = passedAdminFilter;
        this.isAdminDropdownOpen = false;
        this.getAllLeadUser();
    }

    private switchLeadFilterState(state: LEAD_LIST_FILTER_STATE) {
        this.leadListFilterState = state;
    }

    private switchLeadListState(state: LEAD_LIST_STATE) {
        this.leadListState = state;
    }

    toggleFilter() {
        this.filterToggle = !this.filterToggle;
        if(this.filterToggle) {
            this.isAdminDropdownOpen = false;
        }
    }

    toggleAdminDropdown() {
        this.isAdminDropdownOpen = !this.isAdminDropdownOpen;
        if(this.isAdminDropdownOpen) {
            this.filterToggle = false;
        }
    }

    updateListFilter(
        filterState: LEAD_LIST_FILTER_STATE,
        sortingOrderAscending: boolean
    ) {
        this.filterToggle = false;
        this.switchLeadFilterState(filterState);

        if (filterState === LEAD_LIST_FILTER_STATE.ALPHABET) {
            this.filter = sortingOrderAscending
                ? CONTACT_LIST_FILTER.ALPHABET_ASCENDING
                : CONTACT_LIST_FILTER.ALPHABET_DESCENDING;
        }
        if (filterState === LEAD_LIST_FILTER_STATE.CALENDAR) {
            this.filter = sortingOrderAscending
                ? CONTACT_LIST_FILTER.CALENDAR_ASCENDING
                : CONTACT_LIST_FILTER.CALENDAR_DESCENDING;
        }
        this.sortAllLeadData();
    }

    private sortAllLeadData() {
        // Sort the data based on the filter
        const sortData = (data: Record<string, any>, isAscending: boolean, isDate: boolean) => {
            return Object.keys(data)
                .sort((a, b) => {
                    if (isDate) {
                        return isAscending 
                            ? new Date(a).getTime() - new Date(b).getTime()
                            : new Date(b).getTime() - new Date(a).getTime();
                    }
                    return isAscending ? a.localeCompare(b) : b.localeCompare(a);
                })
                .reduce((acc, key) => {
                    acc[key] = data[key];
                    return acc;
                }, {});
        };
    

        // Sort the data based on the alphabet order
        if (this.leadListFilterState === LEAD_LIST_FILTER_STATE.ALPHABET) {
            const sortedDataAlphabet = sortData(
                this.leadData[LEAD_LIST_FILTER_STATE.ALPHABET],
                this.filter === CONTACT_LIST_FILTER.ALPHABET_ASCENDING,
                false
            );
            this.leadData[LEAD_LIST_FILTER_STATE.ALPHABET] = sortedDataAlphabet;
        }

        // Sort the data based on the calendar
        if (this.leadListFilterState === LEAD_LIST_FILTER_STATE.CALENDAR) {
            const sortedDataCalendar = sortData(
                this.leadData[LEAD_LIST_FILTER_STATE.CALENDAR],
                this.filter === CONTACT_LIST_FILTER.CALENDAR_ASCENDING,
                true
            );
            this.leadData[LEAD_LIST_FILTER_STATE.CALENDAR] = sortedDataCalendar;
        }
    }

    private getAllLeadUser() {
        this.switchLeadListState(LEAD_LIST_STATE.LOADING);
        
        let fetchOrgContacts:boolean = false;
        if(this.isAdminUser) {
            fetchOrgContacts = this.adminContactTypeFilter === CONTACT_TYPE.ORGANIZATION_CONTACTS
        }

        this.leadService.getUserLeads(fetchOrgContacts).subscribe({
            next: (response) => {
                // Update the UI if there is no contact
                if(response.count === 0) {
                    this.isLeadCountZero = true;
                    this.switchLeadListState(LEAD_LIST_STATE.FETCHED);
                    return;
                }
                this.leadData = {};
                if (response?.results) {
                    const alphabetData = this.leadData[LEAD_LIST_FILTER_STATE.ALPHABET] || (this.leadData[LEAD_LIST_FILTER_STATE.ALPHABET] = {});
                    const calendarData = this.leadData[LEAD_LIST_FILTER_STATE.CALENDAR] || (this.leadData[LEAD_LIST_FILTER_STATE.CALENDAR] = {});
                    // Extract the starting letter of the first name and capitalize it
                    for (let currentLead of response.results) {
                        const { id, first_name, last_name, email, company, phone, designation, created } = currentLead;

                        // Store Alphabet ordering to leadData Object
                        const startingLetter = currentLead['first_name']?.charAt(0)?.toUpperCase();
                        if (!alphabetData[startingLetter]) alphabetData[startingLetter] = [];
                        alphabetData[startingLetter].push({ id, first_name, last_name, email, company, phone, designation, created });

                        // Store Calendar Year Lead data to leadData Object
                        const currentCalendarItem = new Date(currentLead['created']).toLocaleString('default', {
                            month: 'long',
                            year: 'numeric',
                        });
                        if (!calendarData[currentCalendarItem]) calendarData[currentCalendarItem] = [];
                        calendarData[currentCalendarItem].push({ id, first_name, last_name, email, company, phone, designation, created });
                    }
                    this.sortAllLeadData();
                    this.switchLeadListState(LEAD_LIST_STATE.FETCHED);

                    // Handle behaviour if user is coming from capture screen
                    const contactItemsCheckInterval = setInterval(() => {
                        if(this.contactItemsElements.length === response.count) {
                            this.handleLeadCreationRedirection();
                            clearInterval(contactItemsCheckInterval);
                        }
                    }, 500);
                    setTimeout(() => {
                        clearInterval(contactItemsCheckInterval);
                    }, 5000);
                } else {
                    this.switchLeadListState(LEAD_LIST_STATE.ERROR);
                }
            },
            error: (error) => {
                console.error('ERROR ON FETCHING LEAD:', error);
                this.switchLeadListState(LEAD_LIST_STATE.ERROR);
            },
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
    }
}
